// /clubs/new: mirrors components/home/CreateClubSheet.js + HomeScreen.js:434-795 as a full page.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { BackBar, Page } from "../components/Band";
import Icon from "../components/Icon";
import Confetti from "../components/Confetti";
import { Button, Card, Content, Empty, Field, PageSpinner, Spinner } from "../components/primitives";
import { CLUB_APPLICATION_LIMITS } from "../../../firebase-functions/schema/clubApplication";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import DropZone from "../components/clubs/DropZone";
import LinkEditor from "../components/clubs/LinkEditor";
import useCategories from "../components/clubs/useCategories";
import useCreateClub, { MAX_GALLERY_IMAGES } from "../components/clubs/useCreateClub";
import { categoryEmoji } from "../../../utils/categoryEmoji";
import { pickFiles } from "../lib/files";
import "../styles/clubs-web.css";

function RequestSent({ notes }) {
  return (
    <Card pad className="cc-done">
      <Confetti />
      <Icon name="check" size={36} style={{ color: "var(--green)" }} />
      <h2 style={{ fontSize: 24, fontWeight: 800, marginTop: 8 }}>Request sent</h2>
      <p className="muted" style={{ margin: "6px auto 0", maxWidth: 420 }}>
        Your club creation request has been submitted. A super admin will review it shortly.
        {notes.length > 0 ? ` ${notes.join(" ")}` : ""}
      </p>
      <div style={{ marginTop: 18 }}><Link to="/clubs" className="btn pri" data-testid="createClub.sentBack">Back to clubs</Link></div>
    </Card>
  );
}

export default function CreateClubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userClubs, userClubsLoaded, userClubsError, loadUserClubs } = useData();
  const { categories, loading: catsLoading, failed: catsFailed, reload: reloadCats } = useCategories();
  const c = useCreateClub(user);
  const { form, patch } = c;
  const busy = c.submitting || c.compressing;

  if (!userClubsLoaded) return <Page><BackBar title="Create a club" /><PageSpinner /></Page>;
  if (userClubsError && !(userClubs || []).length) {
    return (
      <Page>
        <BackBar title="Create a club" />
        <Content>
          <Empty icon="alert" title="Couldn't load your clubs">
            <p className="meta" style={{ marginBottom: 12 }}>{String(userClubsError)}</p>
            <Button size="sm" onClick={() => loadUserClubs()} data-testid="createClub.retry">Retry</Button>
          </Empty>
        </Content>
      </Page>
    );
  }

  // HomeScreen.js:1030: one admin club at a time.
  const isAdminOfClub = (userClubs || []).some((x) => x.member_role === "admin");

  const onCategory = (e) => {
    const cat = categories.find((x) => x.id === e.target.value);
    patch({ category: cat?.name || null, category_id: cat?.id || null });
  };

  const body = c.done ? (
    <RequestSent notes={c.done.notes} />
  ) : isAdminOfClub ? (
    <Empty icon="shield" title="Already an admin">
      You are already an admin of a club. You can only be an admin of one club at a time.
      <div style={{ marginTop: 14 }}><Link to="/clubs" className="btn pri" data-testid="createClub.adminBack">Back to clubs</Link></div>
    </Empty>
  ) : (
    <Card pad>
      <form className="cc-form" onSubmit={(e) => { e.preventDefault(); c.submit(); }} noValidate>
        {/* Fields locked while the request is sent: the submitted snapshot is what the super admin sees. */}
        <fieldset disabled={c.submitting} style={{ display: "contents" }}>
        <p className="meta">Your request goes to a VibeLink super admin. You&apos;ll be the club&apos;s admin once it&apos;s approved.</p>

        <div className="row">
          {form.logo ? (
            <img className="logo" src={form.logo} alt="Club logo preview" style={{ "--a": "72px", objectFit: "cover" }} />
          ) : (
            <span className="logo" style={{ "--a": "72px", background: "var(--wash)", color: "var(--g400)" }}><Icon name="camera" size={26} /></span>
          )}
          <Button variant="sec" size="sm" icon="upload" disabled={busy} onClick={async () => c.pickLogo(await pickFiles({ accept: "image/*" }))} data-testid="createClub.logo">
            {form.logo ? "Change logo" : "Upload logo"}
          </Button>
          {form.logo && <Button variant="ghost" size="sm" disabled={busy} onClick={c.removeLogo} data-testid="createClub.removeLogo">Remove photo</Button>}
        </div>

        <Field label="Category *" as="select" data-testid="createClub.category" value={form.category_id || ""} onChange={onCategory} disabled={catsLoading} required
          error={catsFailed ? "Couldn't load categories." : undefined}>
          <option value="">{catsLoading ? "Loading categories…" : "Select Category"}</option>
          {categories.map((x) => <option key={x.id} value={x.id}>{categoryEmoji(x.name) ? `${categoryEmoji(x.name)} ` : ""}{x.name}</option>)}
        </Field>
        {catsFailed && <Button variant="ghost" size="sm" style={{ alignSelf: "flex-start" }} onClick={reloadCats} data-testid="createClub.categoriesRetry">Retry categories</Button>}

        <Field label="Club name *" value={form.name} onChange={(e) => patch({ name: e.target.value })}
          maxLength={CLUB_APPLICATION_LIMITS.name} placeholder="Enter club name" required data-testid="createClub.name" />
        <Field label="Description *" as="textarea" rows={6} value={form.description} onChange={(e) => patch({ description: e.target.value })}
          maxLength={CLUB_APPLICATION_LIMITS.description} placeholder="What does your club do?" required data-testid="createClub.description" />

        <LinkEditor links={form.links} disabled={busy} max={CLUB_APPLICATION_LIMITS.list} testId="createClub.links"
          onAdd={(l) => patch({ links: [...form.links, l] })}
          onRemove={(i) => patch({ links: form.links.filter((_, idx) => idx !== i) })} />

        <div className="field">
          <span>Constitution or other PDF</span>
          {form.pdfs.map((p, i) => (
            <div className="cc-row" key={`${p.uri}`}>
              <span className="pdf-ic"><Icon name="file" size={16} /></span>
              <b className="grow ellip">{p.name || `Document ${i + 1}`}</b>
              <button type="button" className="ibtn" aria-label={`Remove ${p.name || "document"}`} disabled={busy} onClick={() => c.removeAt("pdfs", i)} data-testid={`createClub.pdf.${i}.remove`}><Icon name="x" size={16} /></button>
            </div>
          ))}
          <DropZone icon="file" accept="application/pdf" disabled={busy || form.pdfs.length >= CLUB_APPLICATION_LIMITS.list} label="Drop PDFs here" onFiles={c.addPdfs} testId="createClub.pdfs" />
        </div>

        <div className="field">
          <span>Photos <span className="meta" style={{ fontWeight: 400 }}>({form.gallery.length}/{MAX_GALLERY_IMAGES})</span></span>
          {form.gallery.length > 0 && (
            <div className="thumbs">
              {form.gallery.map((u, i) => (
                <div className="thumb" key={u}>
                  <img src={u} alt={`Gallery photo ${i + 1}`} />
                  <button type="button" className="ibtn" aria-label={`Remove photo ${i + 1}`} disabled={busy} onClick={() => c.removeAt("gallery", i)} data-testid={`createClub.photo.${i}.remove`}><Icon name="x" size={14} /></button>
                </div>
              ))}
            </div>
          )}
          {c.compressing && <div className="row meta"><Spinner label="Processing photos" />Processing photos…</div>}
          <DropZone accept="image/*" disabled={busy || form.gallery.length >= MAX_GALLERY_IMAGES} label="Drop photos here" onFiles={c.addGallery} testId="createClub.photos" />
        </div>

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Button variant="ghost" disabled={c.submitting} onClick={() => navigate("/clubs")} data-testid="createClub.cancel">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="createClub.submit">{c.submitting ? "Submitting…" : "Submit request"}</Button>
        </div>
        </fieldset>
      </form>
    </Card>
  );

  return (
    <Page>
      <BackBar title="Create a club" locked={c.submitting} testId="createClub" />
      <Content>{body}</Content>
    </Page>
  );
}
